import { computedFrom, observable } from "aurelia-binding";
import { Guid } from "../../../utilities/guid";
import { bindable } from "aurelia-templating";
import { autoinject, transient } from "aurelia-dependency-injection";
import { ColorUtilities, Hsv } from "./color-utilities";
import { Reflection } from "../../../utilities/reflection";

@autoinject()
@transient()
export class ColorPicker {

    private intervalForPosition: any;
    private hue: string | number;
    private a: number = 255;

    public input: HTMLElement;
    public canvas: HTMLCanvasElement;
    public bufferCanvas: HTMLCanvasElement;
    public context2d: CanvasRenderingContext2D;
    public context2dBuffer: CanvasRenderingContext2D;
    public colorPalette: HTMLElement;
    public colorPointer: HTMLElement;
    public positionHueSlider: number;
    public isHex = ColorUtilities.isHex;

    @observable
    public container: HTMLElement;

    @bindable
    public identifier: string = Guid.generateGuid();

    @bindable
    label: string = "";

    @bindable
    public info: string = "";

    @bindable
    public infoBottom: string = "";

    @bindable
    public disabled: boolean;

    @bindable
    format: string = "";

    @bindable
    color: string = "ffffff";

    @computedFrom(Reflection.getPropertyPath<ColorPicker>(x => x.color))
    get hexColorFormat() {
        let returnValue = this.color;
        switch (this.format) {
            case "css": {
                if (this.color && this.color.indexOf("#") === 0) {
                    returnValue = this.color.substr(1, this.color.length - 1);
                }
            }
                break;
            default:
                {
                    if (this.color && this.color.indexOf("#") === 0) {
                        returnValue = this.color.substr(1, this.color.length - 1);
                    }
                }
                break;
        }
        return returnValue;
    }

    formatColorString(value: string) {
        let returnValue = value;
        switch (this.format) {
            case "css": {
                returnValue = "#" + value;
            }
                break;
        }
        return returnValue;
    }

    getDimensions(element) {
        const result = { width: element.offsetWidth, height: element.offsetHeight, };
        return result;
    }

    containerChanged(newVal: HTMLElement, oldVal: HTMLElement) {
        if (newVal) {
            newVal.remove();
            setTimeout(()=>{
                document.body.appendChild(newVal);
            });
        }
        if (oldVal) {
            oldVal.remove();
        }
    }

    colorChanged(newValue: string) {
        if (newValue && ColorUtilities.isValid(this.hexColorFormat) && this.canvas) {
            const rect = this.canvas.getBoundingClientRect();
            const rectContainer = this.colorPalette.getBoundingClientRect();
            const rectPointer = this.colorPointer.getBoundingClientRect();
            const offsetY = rect.top - rectContainer.top;
            const offsetX = rect.left - rectContainer.left;
            const width = this.canvas.offsetWidth;
            const height = this.canvas.offsetHeight;
            let x = rectPointer.left + this.getDimensions(this.colorPointer).width / 2 - rect.left + offsetX;
            let y = rectPointer.top + this.getDimensions(this.colorPointer).height / 2 - rect.top + offsetY;
            x = x <= offsetX ? offsetX : (x >= width ? width : x);
            y = y <= offsetY ? offsetY : (y >= height + offsetY ? height + offsetY : y);
            const col = new Hsv(this.hue, Math.round(x / this.canvas.offsetWidth * 100), Math.round(100 - (y - offsetY) / this.canvas.offsetHeight * 100));
            const b1w: any = ColorUtilities.hsv2rgb(col.h, col.s, col.v);
            const color = ColorUtilities.rgbToHex(b1w.r, b1w.g, b1w.b);
            if (this.hexColorFormat && color !== this.hexColorFormat) {
                const rgb = ColorUtilities.getRgbColor(this.hexColorFormat);
                const hsv = ColorUtilities.rgbToHsv(rgb.r, rgb.g, rgb.b);
                this.positionHueSlider = hsv.h;
                x = hsv.s * this.canvas.offsetWidth / 100;
                y = (100 - hsv.v) * this.canvas.offsetHeight / 100 + offsetY;
                this.colorPointer.style.left = x - this.getDimensions(this.colorPointer).width / 2 + "px";
                this.colorPointer.style.top = y - this.getDimensions(this.colorPointer).height / 2 + "px";
            }
        }
    }

    onBlured = () => {
        if (this.color && this.format == "css") {
            const isValid = /^#[a-zA-Z0-9]*$/.test(this.color);
            if (!isValid) {
                this.color = "";
            }
        }
        else {
            const isValid = /^[a-zA-Z0-9]*$/.test(this.color);
            if (!isValid) {
                this.color = "";
            }
        }
    }

    constructor() {
        document.addEventListener("mousedown", (e: Event) => {
            const sourceElement: any = e.srcElement || e.target;
            if (this.container && this.container.contains(sourceElement)) {
                return;
            }
            if (this.container) {
                this.container.style.left = 0 + "px";
                this.container.style.top = 0 + "px";
                this.container.classList.add("hidden");
            }
        });
    }


    setPosition() {
        if (this.input) {
            const inputRect = this.input.getBoundingClientRect();
            const containerRect = this.container.getBoundingClientRect();
            this.container.style.top = inputRect.bottom + containerRect.height > window.innerHeight ? (inputRect.top - containerRect.height) + "px" : inputRect.bottom + "px";
            let left = inputRect.left;
            if (left + containerRect.width > window.innerWidth) {
                left -= (left + containerRect.width - window.innerWidth);
            }
            this.container.style.left = left + "px";
        }
    }

    openColorPalette = () => {
        this.container.classList.remove("hidden");
        this.setPosition();
        setTimeout(() => {
            this.colorChanged(this.color);
        });
        clearInterval(this.intervalForPosition);
        this.intervalForPosition = setInterval(() => {
            this.setPosition();
        }, 100);
        this.container.addEventListener('click', this.stopPropagation);
        document.addEventListener('click', this.closeColorPalette);
    }

    stopPropagation($event) {
        $event.stopPropagation();
    }

    closeColorPalette = () => {
        if (this.container) {
            this.container.classList.add('hidden');
            this.container.addEventListener('click', this.stopPropagation);
            document.removeEventListener('click', this.closeColorPalette);
            clearInterval(this.intervalForPosition);
        }

    }

    attached(): void {
        const mouseMoveFunc = (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const rectContainer = this.colorPalette.getBoundingClientRect();
            const offsetY = rect.top - rectContainer.top;
            const offsetX = rect.left - rectContainer.left;
            const width = this.canvas.offsetWidth + offsetX;
            const height = this.canvas.offsetHeight;
            let x = e.clientX - rect.left + offsetX;
            let y = e.clientY - rect.top + offsetY;
            x = x <= offsetX ? offsetX : (x >= width ? width - 1 : x);
            y = y <= offsetY ? offsetY : (y >= height + offsetY ? height + offsetY : y);
            this.colorPointer.style.left = x - this.getDimensions(this.colorPointer).width / 2 + "px";
            this.colorPointer.style.top = y - this.getDimensions(this.colorPointer).height / 2 + "px";
            const col = new Hsv(this.hue, Math.round(x / width * 100), Math.round(100 - (y - offsetY) / height * 100));
            const b1w: any = ColorUtilities.hsv2rgb(col.h, col.s, col.v);
            this.color = this.formatColorString(ColorUtilities.rgbToHex(b1w.r, b1w.g, b1w.b));
        };
        const mouseUpFunc = () => {
            document.removeEventListener("mousemove", mouseMoveFunc);
        };
        const mouseDownFunc = (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const rectContainer = this.colorPalette.getBoundingClientRect();
            const offsetY = rect.top - rectContainer.top;
            const offsetX = rect.left - rectContainer.left;
            const width = this.canvas.offsetWidth + offsetX;
            const height = this.canvas.offsetHeight;
            let x = e.clientX - rect.left + offsetX;
            let y = e.clientY - rect.top + offsetY;
            x = x <= offsetX ? offsetX : (x >= width ? width - 1 : x);
            y = y <= offsetY ? offsetY : (y >= height + offsetY ? height + offsetY : y);
            this.colorPointer.style.left = x - this.getDimensions(this.colorPointer).width / 2 + "px";
            this.colorPointer.style.top = y - this.getDimensions(this.colorPointer).height / 2 + "px";
            const col = new Hsv(this.hue, Math.round(x / width * 100), Math.round(100 - (y - offsetY) / height * 100));
            const b1w: any = ColorUtilities.hsv2rgb(col.h, col.s, col.v);
            this.color = this.formatColorString(ColorUtilities.rgbToHex(b1w.r, b1w.g, b1w.b));
            document.addEventListener("mousemove", mouseMoveFunc);
        };
        this.canvas.addEventListener("mousedown", mouseDownFunc);
        this.colorPointer.addEventListener("mousedown", mouseDownFunc);
        document.addEventListener("mouseup", mouseUpFunc);
    }

    sliderValueChanged($event: any) {
        this.hue = $event.detail.value;
        this.drawColorPalette(this.hue);
    }

    private drawColorPalette(hue: string | number) {
        if (this.canvas !== null) {
            this.context2d = this.canvas.getContext("2d");
            this.context2dBuffer = this.bufferCanvas.getContext("2d");
            this.context2dBuffer.globalCompositeOperation = "source-over";
            this.context2d.globalCompositeOperation = "source-over";
            const b = this.canvas.width;
            const c = this.canvas.height;
            let d = this.context2d.createLinearGradient(0, c, 0, 0);
            d.addColorStop(0, "#000");
            d.addColorStop(1, "#FFF");
            this.context2d.fillStyle = d;
            this.context2d.fillRect(0, 0, b, c);
            d = this.context2dBuffer.createLinearGradient(b, 0, 0, 0);
            d.addColorStop(0, "hsl(" + hue + ",100%,50%)");
            d.addColorStop(1, "#FFF");
            this.context2dBuffer.fillStyle = d;
            this.context2dBuffer.fillRect(0, 0, b, c);
            const d1 = this.context2d.getImageData(0, 0, b, c);
            const e1 = d1.data;
            const b1 = this.context2dBuffer.getImageData(0, 0, b, c).data;
            let c1 = 0;
            const f1 = e1.length;
            for (; c1 < f1; c1 += 4) {
                e1[c1] = b1[c1] * e1[c1] / 255;
                e1[c1 + 1] = b1[c1 + 1] * e1[c1 + 1] / 255;
                e1[c1 + 2] = b1[c1 + 2] * e1[c1 + 2] / 255;
                e1[c1 + 3] = this.a;
            }
            this.context2d.putImageData(d1, 0, 0);
            const rect = this.canvas.getBoundingClientRect();
            const rectContainer = this.colorPalette.getBoundingClientRect();
            const rectPointer = this.colorPointer.getBoundingClientRect();
            const offsetY = rect.top - rectContainer.top;
            const offsetX = rect.left - rectContainer.left;
            const width = this.canvas.offsetWidth;
            const height = this.canvas.offsetHeight;
            if (width > 0 && height > 0) {
                let x = rectPointer.left + this.getDimensions(this.colorPointer).width / 2 - rect.left + offsetX;
                let y = rectPointer.top + this.getDimensions(this.colorPointer).height / 2 - rect.top + offsetY;
                x = x <= offsetX ? offsetX : (x >= width ? width : x);
                y = y <= offsetY ? offsetY : (y >= height + offsetY ? height + offsetY : y);
                this.colorPointer.style.left = x - this.getDimensions(this.colorPointer).width / 2 + "px";
                this.colorPointer.style.top = y - this.getDimensions(this.colorPointer).height / 2 + "px";
                const hsv = new Hsv(this.hue, Math.round(x / width * 100), Math.round(100 - (y - offsetY) / height * 100));
                const rgb: any = ColorUtilities.hsv2rgb(hsv.h, hsv.s, hsv.v);
                const rgbCurrentColor = ColorUtilities.getRgbColor(this.hexColorFormat);
                const hsvCurrentColor = ColorUtilities.rgbToHsv(rgbCurrentColor.r, rgbCurrentColor.g, rgbCurrentColor.b);

                if (hsvCurrentColor.h !== hsv.h || hsvCurrentColor.s !== hsv.s || hsvCurrentColor.v !== hsv.v) {
                    this.color = this.formatColorString(ColorUtilities.rgbToHex(rgb.r, rgb.g, rgb.b));
                }
            }
        }

    }

    @computedFrom(Reflection.getPropertyPath<ColorPicker>(x => x.color))
    get colorText(): string {
        if (this.hexColorFormat) {
            const rgb = ColorUtilities.getRgbColor(this.hexColorFormat);
            const o = Math.round(((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000);
            if (o > 125) {
                return "black";
            } else {
                return "white";
            }
        }
        return "grey";
    }

}
